import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/contexts";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  // Explains why the user landed back here (password changed, deactivated…).
  useEffect(() => {
    const reason = sp.get("reason");
    if (reason) { toast.error(reason); setSp({}, { replace: true }); }
  }, [sp, setSp]);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const openForgot = () => {
    setForgotEmail(email || "");
    setForgotSent(false);
    setForgotOpen(true);
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Please enter your account email"); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch {
      // Backend intentionally always returns 200 for enumeration safety, but
      // in case of a transport failure we still show the generic ack so we
      // never leak whether the email exists.
      setForgotSent(true);
    } finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-background">
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzdW5saWdodCUyMGFncmljdWx0dXJlJTIwZm9vZCUyMGluZ3JlZGllbnRzfGVufDB8fHx8MTc4OTQ2NzA3NXww&ixlib=rb-4.1.0&q=85"
          alt="Sunlit crops"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a2a1a]/70 via-[#355E3B]/50 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h1 className="font-heading font-extrabold text-4xl lg:text-5xl leading-tight max-w-lg">
            Cultivating trust in every ingredient.
          </h1>
          <p className="mt-4 text-white/85 max-w-md">
            The B2B CRM built for Sun Nutrafoods' sales team — manage contacts, pipeline and performance from one place.
          </p>
        </div>
      </div>
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <img
              src="/logo.webp"
              alt="Sun Nutrafoods"
              data-testid="brand-logo"
              className="h-[84px] w-auto object-contain -ml-3"
            />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
              Customer Relationship Management
            </div>
          </div>
          <h2 className="font-heading text-2xl font-bold">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1">Sign in to access your pipeline.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" data-testid="login-email"
                value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={openForgot}
                  className="text-xs text-primary hover:underline font-medium">
                  Forgot password?
                </button>
              </div>
              <Input id="password" type="password" data-testid="login-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <div className="mt-8 p-4 bg-secondary/50 border border-border rounded-md text-xs text-neutral-600 space-y-1">
            <div className="font-semibold text-neutral-800 mb-1">Need access?</div>
            <div>Ask your Manager to create an account for you or to reset your password.</div>
            <div>You can change your own password any time from <b>Account Settings</b>.</div>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) setForgotSent(false); }}>
        <DialogContent className="max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset your password</DialogTitle>
          </DialogHeader>
          {!forgotSent ? (
            <form onSubmit={submitForgot} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the email on your Sun Nutrafoods CRM account. We'll email you a link to set a new password.
                Sales staff will have their Manager notified instead.
              </p>
              <div>
                <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider">Account email</Label>
                <Input id="forgot-email" type="email" data-testid="forgot-email"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
                  className="mt-1.5" placeholder="you@sunnutrafoods.com" autoFocus />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="forgot-submit" disabled={forgotBusy}
                  className="bg-primary hover:bg-primary/90 text-white">
                  {forgotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3 text-sm" data-testid="forgot-sent">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-900">
                <div className="font-semibold mb-1">Request received</div>
                <div className="text-xs">
                  If <span className="font-mono">{forgotEmail}</span> is on file, you'll receive an email shortly:
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    <li><b>Managers</b> get a reset link (valid 1 hour, single use).</li>
                    <li><b>Sales staff</b> — your Managers have been notified and one will reset it for you.</li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Didn't get the email in a few minutes? Check spam, or contact your Manager.
              </p>
              <div className="flex justify-end">
                <Button onClick={() => setForgotOpen(false)} className="bg-primary hover:bg-primary/90 text-white">
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
